/**
 * A one-slot registry so the `ImageNodeView` can trigger a retry.
 *
 * The node view is rendered by ProseMirror, not by React's tree, so it cannot reach the
 * page's context through props or a provider — and passing the editor's `UploadContext`
 * through the extension options would bake `pageId` into the schema, which is exactly the
 * kind of thing that makes the extension list untestable.
 *
 * One editor is mounted at a time (`111-frontend-architecture.md` §7 — one page route, one
 * editor), so one slot is honest. If a second editor is ever mounted concurrently, this
 * becomes a `Map` keyed by the editor instance; it is not one today because pretending to
 * support a case that does not exist is how you get a registry nobody ever cleans up.
 */
import type { Editor } from '@tiptap/react';
import { retryUpload, type UploadContext } from '@/features/editor/uploads';

let current: { editor: Editor; ctx: UploadContext } | null = null;

export function registerUploadTarget(editor: Editor, ctx: UploadContext): () => void {
  current = { editor, ctx };
  return () => {
    if (current?.editor === editor) current = null;
  };
}

export async function retryImageUpload(uploadId: string): Promise<void> {
  if (current === null) return;
  await retryUpload(current.editor, current.ctx, uploadId);
}
